import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, Check, Phone } from "lucide-react";
import { MaskLines, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", email: "", phone: "", timeframe: "", message: "", privacy_consent: false };
const timeframes = ["In 1 Jahr", "In 1–3 Jahren", "In mehr als 3 Jahren", "Noch offen"];

const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-white/75">{label}</span>
    <input id={id} data-testid={`contact-form-${id}`} className="mt-2 w-full border-0 border-b border-white/25 bg-transparent py-3 text-lg text-[var(--stone)] outline-none transition-colors placeholder:text-white/40 focus:border-[#E3A68F]" {...props} />
  </label>
);

const ThankYou = ({ name, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} data-testid="contact-thank-you" className="border border-white/15 p-8 sm:p-12">
    <span className="grid h-14 w-14 place-items-center rounded-full bg-[#E3A68F] text-[var(--ink)]"><Check className="h-6 w-6" /></span>
    <p className="font-serif-d mt-8 text-4xl leading-tight sm:text-5xl">Vielen Dank{name ? `, ${name}` : ""}.</p>
    <p className="mt-5 max-w-md text-base leading-relaxed text-white/80 md:text-lg">Ihre Nachricht ist bei uns angekommen. Sanaz und Ghassem lesen jede Anfrage persönlich und melden sich innerhalb von zwei Werktagen bei Ihnen.</p>
    <button type="button" onClick={onReset} data-testid="contact-new-message" className="mt-8 text-sm text-white/85 underline decoration-[#E3A68F] underline-offset-4 hover:text-[#E3A68F]">Weitere Nachricht senden</button>
  </motion.div>
);

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle");
  const [sentName, setSentName] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setState("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setSentName(form.name.trim().split(" ")[0]);
      setState("done");
      setForm(empty);
    } catch {
      setState("idle");
      toast.error("Senden fehlgeschlagen. Bitte prüfen Sie Ihre Angaben.");
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="grain relative overflow-hidden bg-[var(--ink)] text-[var(--stone)]">
      <div className="mx-auto grid max-w-[1440px] gap-16 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-40">
        <div className="lg:col-span-5">
          <p className="eyebrow !text-[#E3A68F]">06 — Kontakt</p>
          <h2 className="font-serif-d mt-8 text-5xl leading-[1] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskLines inView lines={["Sprechen wir", <span key="i" className="italic text-[#E3A68F]">über morgen.</span>]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/80 md:text-lg">Ein unverbindliches Erstgespräch, vertraulich und persönlich. Erzählen Sie uns kurz von Ihrem Unternehmen und Ihrem Zeitplan.</p>
            <a href="mailto:kontakt@aufbruchwerk.de" data-testid="contact-email-link" className="font-serif-d mt-10 inline-block border-b border-white/30 pb-1 text-2xl text-[var(--stone)] transition-colors hover:border-[#E3A68F] hover:text-[#E3A68F] sm:text-3xl">kontakt@aufbruchwerk.de</a>
            <a href="tel:+491796735156" data-testid="contact-call-button" className="group mt-8 flex w-fit items-center gap-4 rounded-full border border-white/25 py-2 pl-2 pr-6 transition-[border-color,background-color] duration-300 hover:border-[#E3A68F] hover:bg-white/5">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[#E3A68F] text-[var(--ink)] transition-transform duration-500 group-hover:-rotate-12"><Phone className="h-4 w-4" /></span>
              <span>
                <span className="font-mono-d block text-[11px] uppercase tracking-[0.2em] text-white/70">Direkt anrufen</span>
                <span className="block text-lg text-[var(--stone)]">+49 179 6735156</span>
              </span>
            </a>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
          {state === "done" ? <ThankYou name={sentName} onReset={() => setState("idle")} /> : (
          <form onSubmit={submit} data-testid="contact-form" className="grid gap-8 sm:grid-cols-2">
            <Field label="Name *" id="name" required value={form.name} onChange={set("name")} placeholder="Ihr Name" />
            <Field label="Unternehmen" id="company" value={form.company} onChange={set("company")} placeholder="Firma" />
            <Field label="E-Mail *" id="email" type="email" required value={form.email} onChange={set("email")} placeholder="name@firma.de" />
            <Field label="Telefon" id="phone" value={form.phone} onChange={set("phone")} placeholder="Optional" />
            <div className="sm:col-span-2">
              <span className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-white/75">Geplante Übergabe</span>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-form-timeframe">
                {timeframes.map((t) => (
                  <button type="button" key={t} data-testid={`timeframe-${t.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setForm({ ...form, timeframe: t })}
                    className={`rounded-full border px-4 py-2 text-sm transition-[background-color,color,border-color] duration-300 ${form.timeframe === t ? "border-[#E3A68F] bg-[#E3A68F] text-[var(--ink)]" : "border-white/25 text-white/85 hover:border-white/60"}`}>{t}</button>
                ))}
              </div>
            </div>
            <label className="block sm:col-span-2">
              <span className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-white/75">Nachricht *</span>
              <textarea data-testid="contact-form-message" required rows={4} value={form.message} onChange={set("message")} placeholder="Was sollen wir über Ihr Unternehmen wissen?" className="mt-2 w-full resize-none border-0 border-b border-white/25 bg-transparent py-3 text-lg text-[var(--stone)] outline-none placeholder:text-white/40 focus:border-[#E3A68F]" />
            </label>
            <label className="flex cursor-pointer items-start gap-3 sm:col-span-2" data-testid="contact-form-consent-label">
              <input type="checkbox" required checked={form.privacy_consent} onChange={(e) => setForm({ ...form, privacy_consent: e.target.checked })} data-testid="contact-form-consent" className="mt-1 h-4 w-4 shrink-0 accent-[#E3A68F]" />
              <span className="text-sm leading-relaxed text-white/80">Ich habe die <Link to="/datenschutz" className="underline decoration-[#E3A68F] underline-offset-4 hover:text-[#E3A68F]" data-testid="contact-form-privacy-link">Datenschutzerklärung</Link> gelesen und bin einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und per E-Mail an Aufbruchwerk übermittelt werden. Ich kann meine Einwilligung jederzeit widerrufen. *</span>
            </label>
            <div className="flex flex-wrap items-center justify-between gap-4 sm:col-span-2">
              <p className="max-w-xs text-xs text-white/65">Ihre Angaben behandeln wir vertraulich.</p>
              <button type="submit" disabled={state === "loading"} data-testid="contact-form-submit" className="group flex items-center gap-3 rounded-full bg-[var(--stone)] py-2 pl-6 pr-2 text-sm text-[var(--ink)] transition-[background-color] duration-300 hover:bg-[#E3A68F] disabled:opacity-60">
                Anfrage senden
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[var(--ink)] text-[var(--stone)] transition-transform duration-500 group-hover:rotate-45">
                  {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                </span>
              </button>
            </div>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
